import { Moment } from "moment";

import { HarvestUnitValue } from "consts";

import { ENUM_FERTILIZER_UNITS, EVALUATION_METRICS, PRACTICE_SLUG, PracticeSlug, TILLAGE_SOIL_MIXING_TYPE } from "models";

export type Field = {
  id: string;
  flik: string;
  name: string;
  number: string;
  reviewed: boolean;
  size: number | null;
  updatedAt: string;
  isInRedzone: boolean;
  location?: Coordinates;
};

export type FieldsListResponse = {
  fields: {
    assignedModuleIds: string[];
    id: string;
    name: string;
    reviewed: boolean | null;
    size: number | null;
    isInRedzone: boolean;
  }[];
  page: number;
  totalItemsCount: number;
  itemsPerPageCount: number;
  totalPageCount: number;
};

export type Coordinates = {
  lat: number;
  lng: number;
};

type MethodCrop = {
  crop: string | number;
  year: number;
};

export type FieldDetails = {
  id: string;
  name: string;
  nearby: string[];
  number: string;
  size: number;
  soilType: string;
  soilPoints: number;
  humusContent: string;
  bulkDensity: number;
  soilDate: string;
  location: Coordinates;
  crops: MethodCrop[];
  flik: string;
  reviewed: boolean;
  exceedsHectareLimit: boolean;
  isInRedzone: boolean;
  regionId: string;
};

type FieldGeometry = {
  center: Coordinates;
  geometry: Coordinates[];
  type: ShapeType;
};

type FieldRedzoneRequest = {
  id: string;
  state: string;
  accurate: boolean;
  confirmed: boolean;
  geometry?: FieldGeometry | null;
};

export type AddFieldRequestBody = {
  name: string;
  nearby?: string[];
  number?: string;
  size: number;
  soilType?: string;
  soilPoints?: number;
  humusContent?: string;
  soilDate?: string;
  geometry: FieldGeometry;
  crops?: MethodCrop[];
  flik?: string;
  location: Coordinates;
  redzone?: FieldRedzoneRequest | null;
  isInRedzone?: boolean;
};

export type FieldRedzone = {
  id: string;
  fieldId: string;
  redzoneId: string;
  year: number;
  confirmed: boolean | null;
  field: {
    id: string;
    number: string;
    name: string;
  };
};

export type RedzoneException = {
  description: string;
  disabled: boolean;
  id: string;
  key: string | null;
};

export type Redzone = {
  id: string;
  state: string;
  accurate: boolean;
  geometry: string;
  createdAt: string;
  updatedAt: string;
};

export type RedzoneResponse = {
  id: string;
  state: string;
  accurate: boolean;
  geometry: Coordinates[];
};

export type ShapeType = "shapeFile" | "googlePolygon" | "digifarm" | "square";

export type FieldShape = {
  fieldId: string;
  geometry: Coordinates[];
  center: Coordinates;
  type: ShapeType;
  manual?: boolean;
};

export type ResolveFieldGeometryResponse = {
  /** null when the region could not be determined. */
  regionId: string | null;
};

export type FieldListField = {
  id: string; // uuid
  isInRedzone: boolean;
  location: Coordinates;
  name: string;
  redzones: {
    confirmed: boolean | null;
    redzoneId: string;
  }[];
  regionId: string;
  size: number;
  // ascending, e.g. [2021, 2022, 2023] — empty if the field has no crops participation or the program has no baseline
  baselineYears?: number[];
};

export enum CROP_TYPE_FCPO {
  MAIN = "MAIN",
  COVER = "COVER",
  UNDERSOWING = "UNDERSOWING",
}

export type PracticeBundle = {
  active: boolean; // must be true if confirmed is true
  hasConfirmedData: boolean;
  slug: PracticeSlug;
  startYear: number | null;
  discontinuedYear: number | null;
  reactivatedYear: number | null;
  enrollable: boolean;
};

export type ActivePracticeBundle = PracticeBundle & { hasMissingData: boolean };

export type PracticeFCPO = {
  id?: string;
  startDate?: string | null;
  endDate: string | null;
  type: Extract<PracticeSlug, "mineralFertilizers" | "organicFertilizers" | "tillages">;
  assignedPracticeId: string; // uuid
  isConfirmed: boolean;
  year?: number;
  cropId?: string; // maybe only for tillage
  payout?: number; // also not implementable now
  verificationStatus?: VerificationStatus;
  reviewedDataPoints?: ReviewedDataPoint[] | null;
  selectionType?: string | null;
  soilPlus3DataCollection: boolean;
  tillageDepthSoilPlus3DataCollection?: boolean; // only relevant for tillage
  applicationDatesSoilPlus3DataCollection?: boolean; // only relevant for tillage and organic fertilizer
  // start of data just for organic fertilisers
  nValue?: number;
  amount?: number;
  // Display unit the farmer picked for `amount`. m³ and t are treated as 1:1, so this never changes the stored value.
  amountUnit?: ENUM_FERTILIZER_UNITS | null;
  selfProduced?: boolean | null;
  applicationType?: string; // uuid
  tillageDepth?: number; // only relevant for tillage
  tillageSoilMixingType?: TILLAGE_SOIL_MIXING_TYPE; // only relevant for tillage
  seedingPracticeId?: string; // only crops have a seedingPracticeId
  sanityNoteNitrogen?: string | null; // only relevant for organic ferts
  fallbackDates: {
    startDate: string | null;
    endDate: string | null;
  };
};

export const VERIFICATION_STATUS = {
  approved: "approved",
  autoApproved: "auto-approved",
  pending: "pending",
  rejected: "rejected",
} as const;

export type VerificationStatus = (typeof VERIFICATION_STATUS)[keyof typeof VERIFICATION_STATUS] | null;

export const REVIEWED_DATA_POINT_CROP = {
  cropType: "cropType",
  yield: "yield",
  residue: "residue",
  dryMass: "dryMass",
  seedingDates: "seedingDates",
  harvestDates: "harvestDates",
} as const;

export type ReviewedDataPointCrop = (typeof REVIEWED_DATA_POINT_CROP)[keyof typeof REVIEWED_DATA_POINT_CROP];

export const REVIEWED_DATA_POINT_ORGANIC_FERTILIZER = {
  fertilizerType: "fertilizerType",
  amount: "amount",
  nValue: "nValue",
  applicationDates: "applicationDates",
  selfProduced: "selfProduced",
  application: "application",
} as const;

export type ReviewedDataPointOrganicFertilizer = (typeof REVIEWED_DATA_POINT_ORGANIC_FERTILIZER)[keyof typeof REVIEWED_DATA_POINT_ORGANIC_FERTILIZER];

export type ReviewedDataPoint = ReviewedDataPointCrop | ReviewedDataPointOrganicFertilizer;

export type PracticeFCPORequest = {
  startDate?: string | null;
  endDate: string | null;
  assignedPracticeId: string; // uuid
  type: Extract<PracticeSlug, "mineralFertilizers" | "organicFertilizers" | "tillages">;
  // start of data just for fertilizers
  amount?: number;
  // start of data just for organic fertilizers
  amountUnit?: ENUM_FERTILIZER_UNITS | null;
  nValue?: number;
  selfProduced?: boolean | null;
  applicationType?: string; // uuid
  tillageDepth?: number; // only relevant for tillage
  verificationStatus?: VerificationStatus;
  sanityNoteNitrogen?: string | null; // only relevant for organic ferts
};

export type CropFCPO = CropFCPORequest & {
  id: string;
  cropId: string; // uuid
  cropName: string;
  cropType: CROP_TYPE_FCPO;
  fallbackDates: {
    seedingStartDate: string | null;
    harvestEndDate: string | null;
  };
  growingDatesSoilPlus3DataCollection?: boolean;
  harvestEndDate: string | null;
  harvestStartDate: string | null;
  hasResidue: boolean | null;
  isConfirmed: boolean | null;
  isSecondaryData?: boolean | null;
  sanityNoteResidue?: string | null;
  sanityNoteYield?: string | null;
  seedingEndDate: string | null;
  seedingPracticeId?: string; // Seeding verification status is now part of crop
  seedingPracticeType: string | null; // uuid
  seedingSelectionType?: string | null;
  seedingSoilPlus3DataCollection?: boolean;
  seedingStartDate: string | null;
  seedingVerificationStatus?: VerificationStatus; // Seeding verification status is now part of crop
  selectionType?: string | null;
  translationSlug: string;
  updatedAt?: string;
  verificationStatus?: VerificationStatus;
  reviewedDataPoints?: ReviewedDataPoint[] | null;
  wasHarvested?: boolean | null;
  year?: number;
  yieldPerHa: number | null;
  yieldPerHaUnit: HarvestUnitValue | null;
  yieldSoilPlus3DataCollection?: boolean;
};

export type CropFCPORequest = {
  cropId: string;
  cropMixId?: string;
  cropType: CROP_TYPE_FCPO;
  harvestEndDate: string | null;
  harvestStartDate?: string | null;
  hasResidue: boolean | null;
  isSecondaryData?: boolean | null;
  mandatory?: boolean | null;
  redzoneExceptionExplanation?: string | null;
  redzoneExceptionId?: string | null;
  sanityNoteResidue?: string | null;
  sanityNoteYield?: string | null;
  seedingEndDate?: string | null;
  seedingPracticeType?: string | null; // uuid
  seedingStartDate: string | null;
  seedingVerificationStatus: VerificationStatus;
  speciesCount?: number | null;
  wasHarvested?: boolean | null;
  yieldPerHa?: number | null;
  yieldPerHaUnit?: string | null;
};

export type FertilizerManagement = { implements4R: boolean; year: number; fieldCropId: string };

export type FieldPracticeResponse = {
  availablePracticeBundles: PracticeBundle[]; // saved on a per-field level
  crops: CropFCPO[];
  fieldHasFailedVerification: boolean;
  practices: PracticeFCPO[];
  fertilizerManagements: FertilizerManagement[];
  fieldId: string;
};

export type FieldPracticeRowTimestamps = {
  id: string;
  updatedAt: string;
};

export type CropRotationStepCropIdOption = {
  cropId: string;
  translationSlug: string | null;
  count: number;
};

export type CropRotationStepCropOption = {
  translationSlug: string;
  count: number;
};

export type CropRotationStep = {
  position: number;
  subCategory: string | null;
  family: string | null;
  exampleCropId: string | null;
  cropIdOptions: CropRotationStepCropIdOption[];
  exampleCropTranslationSlug: string | null;
  cropTranslationSlugOptions: CropRotationStepCropOption[];
  cultivationBreakYears: number | null;
};

export type CropRotationAnalysis = {
  cycleLength: number | null;
  label: string;
  canonicalLabel: string;
  pattern: CropRotationStep[];
  canonicalPattern: CropRotationStep[];
  confidence: "high" | "medium" | "low";
  monoculture: boolean;
  irregular: boolean;
  windowTooShort: boolean;
  yearsAnalysed: number[];
  yearsUsed: number;
};

export type CropRotationResult = CropRotationAnalysis & {
  fieldId: string;
  setAside: boolean;
  setAsideYears: number[];
  rotationBeforeSetAside: CropRotationAnalysis | null;
};

export type FieldCropRecommendation = {
  cropId: string;
  translationSlug: string;
  hasResidue: boolean;
  payoutDifference: {
    withResidue: number | null;
    withoutResidue: number;
  };
};

type FieldCropRecommendationGroup = {
  seedingDate: string | null;
  harvestDate: string | null;
  recommendations: FieldCropRecommendation[];
};

export type CategorizedFieldCropRecommendation = {
  year: number;
  catchCrop: FieldCropRecommendationGroup;
  undersowing: FieldCropRecommendationGroup;
};

export type FieldCropRecommendationsResponse = { [fieldId: string]: CategorizedFieldCropRecommendation[] };

export type BasicFieldDetails = {
  id: string;
  name: string;
  size: number;
  location: {
    lat: number;
    lng: number;
  };
  reviewed: boolean;
  isInRedzone: boolean;
};

export const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

export const OTHER_REDZONE_EXCEPTION_KEY = "other";

export type FieldEvaluationMetricCorrectionItem = {
  id: string;
  year: number;
  value: number;
};

export type FieldEvaluationMetricCorrection = {
  [key in EVALUATION_METRICS]: Array<FieldEvaluationMetricCorrectionItem>;
};

export type FieldEvaluationMetricCorrectionsResponse = {
  [fieldId: string]: FieldEvaluationMetricCorrection;
};

export type FieldSizeUpdate = {
  id: string;
  farmId: string;
  fieldId: string;
  previousSize: number;
  newSize: number;
  updateMethod: string;
  createdAt: Date;
  updatedAt: Date;
};

export type Geometry = FieldShape & { geometry: Coordinates[] };

export type Area = {
  center: Coordinates;
  area: number;
  geometry: Geometry;
};

export type FieldPracticeFilterOptionsResponse = {
  cropIds: string[];
  years: number[];
};

export type ItemWithMissingData = {
  id: string;
  startTime: Moment;
};

export type ItemsWithMissingData = {
  cropsWithMissingGrowingDates: ItemWithMissingData[];
  cropsWithMissingPractices: ItemWithMissingData[];
  practicesWithMissingData: ItemWithMissingData[];
  missingCrops: ItemWithMissingData[];
  cropsWithDataToConfirm: ItemWithMissingData[];
  cropsWithVerificationEditingRequests: ItemWithMissingData[];
  coverCropsWithMissingRedzoneException: ItemWithMissingData[];
  cropsWithMissingSecondaryDataStatus: ItemWithMissingData[];
};

export type ItemsWithMissingDataByFieldId = {
  [fieldId: string]: ItemsWithMissingData;
};

export type CropsWithMissingHistoricYieldDataByCropId = {
  [cropId: string]: {
    translationSlug: string;
    years: {
      [year: number]: {
        entries: CropFCPO[];
        defaultValue: number | null;
        sanityNote: string | null;
        isCompleted: boolean;
        isPartOfSoilPlus3DataCollection: boolean;
      };
    };
  };
};

export type CropsWithMissingHistoricalPracticeDataItem = {
  fieldId: string;
  crop: CropFCPO;
  previousMainCrop?: CropFCPO;
  hasMissingData: boolean;
  isTillageDepthRequired: boolean;
  assignedPractices: {
    [PRACTICE_SLUG.TILLAGES]: PracticeFCPO[] | null;
    [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: PracticeFCPO[] | null;
    [PRACTICE_SLUG.SEEDINGS]: string[] | null;
  };
};

export type CropsWithMissingHistoricalPracticeDataByYear = {
  [year: number]: Array<CropsWithMissingHistoricalPracticeDataItem>;
};

// sibling of CropsWithMissingHistoricalPracticeDataItem: adds an editability signal and mineral fertilizer data for
// the unconditioned all-fields data table view, without touching the shape the historical (Data Gap Flow) view already relies on
export type AllCropsPracticeDataItem = Omit<CropsWithMissingHistoricalPracticeDataItem, "assignedPractices"> & {
  isEditable: boolean;
  assignedPractices: CropsWithMissingHistoricalPracticeDataItem["assignedPractices"] & {
    [PRACTICE_SLUG.MINERAL_FERTILIZERS]: PracticeFCPO[] | null;
  };
};

export type AllCropsPracticeDataByYear = {
  [year: number]: Array<AllCropsPracticeDataItem>;
};

export type EditingRequestPlaceholderYears = {
  [CROP_TYPE_FCPO.MAIN]: number[];
  [CROP_TYPE_FCPO.COVER]: number[];
  [CROP_TYPE_FCPO.UNDERSOWING]: number[];
};

export type AddFieldPracticeParticipationsRequest = { fieldId: string; practiceSlugs: string[] };

export type AddFieldPracticeParticipationsResponseBody = {
  success: boolean;
  skippedPractices: { fieldId: string; practiceSlug: PracticeSlug; reasons: string[] }[];
};

export type BulkUpdateCropYieldRequestBody = {
  farmFieldCropId: string;
  yieldPerHa: number | null;
  yieldPerHaUnit: HarvestUnitValue | null;
  sanityNoteYield?: string | null;
};

export type UpdatedCropYield = {
  farmFieldCropId: string;
  yieldPerHa: number | null;
  yieldPerHaUnit: HarvestUnitValue | null;
  sanityNoteYield: string | null;
  yieldSoilPlus3DataCollection: boolean;
};

// crops whose yield was already set and isn't flagged for Soil+3.0 data collection are silently skipped by the
// backend — skippedCropIds tells the frontend exactly which ones, instead of the "success: true" hiding it
export type BulkUpdateCropYieldResponseBody = {
  success: boolean;
  skippedCropIds: string[];
  updatedCrops: UpdatedCropYield[];
};

export type BulkUpdateSeedingPracticesRequestBody = {
  farmFieldCropIds: string[];
  seedingPracticeType: string;
};

export type BulkUpdateOrganicFertilizerPracticesRequestBody = {
  farmFieldCropIds: string[];
  practices: PracticeFCPORequest[];
};

export type BulkUpdateMineralFertilizerPracticesRequestBody = {
  farmFieldCropIds: string[];
  practices: PracticeFCPORequest[];
};

export type BulkUpdateTillagePracticesRequestBody = {
  farmFieldCropIds: string[];
  practices: PracticeFCPORequest[];
};

export type BulkUpdateCropDatesRequestBodyItem = {
  fieldCropId: string;
  seedingStart: string;
  seedingEnd: string;
  harvestStart: string;
  harvestEnd: string;
};

export type BulkUpdateCropDatesRequestBody = BulkUpdateCropDatesRequestBodyItem[];

// same skip semantics as BulkUpdateCropYieldResponseBody, for crops whose growing period was already set and
// isn't flagged for Soil+3.0 data collection
export type BulkUpdateCropDatesResponseBody = {
  success: boolean;
  skippedCropIds: string[];
};
